/**
 * Company Service
 * 
 * Service for managing Company records in the ICFR system.
 * Handles all company-related API calls.
 */
import { apiRequest } from './apiClient';
import { EmptyResponse } from './types';

export interface Company {
    companyId: string;
    companyName: string;
    companyDescription: string;
    internalAdminName: string;
    createdAt: string;
}

interface CompanyBackend {
    Id: string;
    CompanyName: string;
    CompanyDescription: string;
    InternalAdminName: string;
    CreatedAt: string;
}

interface GetCompaniesBackendResponse {
    result: CompanyBackend[];
    count: number;
}

interface DuplicateCompanyRequest {
    companyId: string;
    companyName: string;
    annualReportId: string;
}

interface CreateCompanyRequest {
    companyName: string;
}

interface UpdateCompanyRequest {
    companyId: string;
    companyName: string;
}

interface GetCompanyImageResponse {
  data: CompanyImageBackend;
}

interface CompanyImageBackend {
  downloadUrl: string;
}

const mapBackendToCompany = (backend: CompanyBackend): Company => {
    return {
        companyId: backend.Id,
        companyName: backend.CompanyName,
        companyDescription: backend.CompanyDescription,
        internalAdminName: backend.InternalAdminName,
        createdAt: backend.CreatedAt
    }
}

// ==================== API REQUEST/RESPONSE TYPES ====================
export interface GetCompaniesRequest {
    companyName?: string;
    internalAdminName?: string;
    skip?: number;
    take?: number;
}

export interface GetCompaniesResponse {
    data:Company[];
    totalCount:number;
}

// ==================== API FUNCTIONS ====================

/**
 * Get all gaps for an annual report
 * Default sort: Descending by GapDate (Newest first)
 * Note: Status filter is handled by backend by default
 * Optional processId/cycleId filters for navigation from Process Hierarchy
 */
export const getAllCompanies = async (request: GetCompaniesRequest): Promise<GetCompaniesResponse> => {
  try {
    // Build where clause for filtering
    const whereConditions: { field: string; operator: string; value: string }[] = [];
    
    if (request.companyName) {
      whereConditions.push({
        field: 'CompanyName',
        operator: 'equal',
        value: request.companyName,
      });
    }
    
    if (request.internalAdminName) {
      whereConditions.push({
        field: 'InternalAdminName',
        operator: 'equal',
        value: request.internalAdminName,
      });
    }
    

    const requestBody = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
      // Sort by Company Name (alphabetically)
      sorted: [{ name: 'CompanyName', direction: 'ascending' as const }],
      // Add where conditions if any filters are specified
      ...(whereConditions.length > 0 && { where: whereConditions }),
    };

    const response = await apiRequest<GetCompaniesBackendResponse, typeof requestBody>(
      'POST',
      `/admin/companies/index-grid`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = response.result.map(mapBackendToCompany);
    
    return {
      data: mappedData,
      totalCount: mappedData.length, // Update count to reflect filtered results
    };
  } catch (error) {
    console.error('Failed to fetch companies:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch companies',
    };
  }
};

export const duplicateCompany = async (request: DuplicateCompanyRequest): Promise<EmptyResponse> => {
  try {
    const requestBody = {
      companyId: request.companyId,
      companyName: request.companyName,
      annualReportId: request.annualReportId
    }

    return await apiRequest<EmptyResponse, DuplicateCompanyRequest>(
      'POST',
      `/admin/duplicate-company`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to duplicate company',
    };
  }
};

export const createCompany = async (request: CreateCompanyRequest): Promise<EmptyResponse> => {
  try {
    const requestBody = {
      companyName: request.companyName,
    }

    return await apiRequest<EmptyResponse, CreateCompanyRequest>(
      'POST',
      `/admin/company`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create company',
    };
  }
};

export const getCompanyImageBase64 = async (companyId: string): Promise<GetCompanyImageResponse> => {
  try {


    return await apiRequest<GetCompanyImageResponse, never>(
      'GET',
      `/file-uploads/company-logo/presigned-url/${companyId}`,
      undefined,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to retrieve company image',
    };
  }
};


export const updateCompany = async (request: UpdateCompanyRequest): Promise<EmptyResponse> => {
  try {
    const requestBody = {
      companyId: request.companyId,
      companyName: request.companyName
    }

    return await apiRequest<EmptyResponse, UpdateCompanyRequest>(
      'PUT',
      `/admin/company`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update company',
    };
  }
};


export const deleteCompany = async (companyId: string): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, string>(
      'DELETE',
      `/admin/company/${companyId}`,
      undefined,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete company',
    };
  }
};